import {
	HubDataSource,
	HubItemCategoryModel,
	HubItemDataSource,
	HubItemGroupHubSidebarNodeModel
} from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { outOfContextCall } from "client/Misc/reactUtils";
import { createContext } from "react";
import { TagViewModel } from "client/Api/Tags/tags";
import { SelectedAuthor } from "./Announcements/Overview/Overview";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { CommentsSortPreference, CommentWithRepliesModel } from "./HubCommentSection/HubCommentSection";
import { ImageAttributionViewModel, SortDirection } from "client/Api/common";
import { never } from "client_legacy/ndaw/errors";
import {
	getHubImageUrl,
	HubCommentModel,
	HubItemCategoryNodeKind,
	HubItemElementKind,
	HubItemMediaKind
} from "client/Api/Hub/common";
import {
	groupBy,
	partition,
	sortBy
} from "client_legacy/ndaw/arrayHelpers";
import { HubItemLayoutDetailsViewModel, HubItemMediaDetailsViewModel } from "client/Api/Hub/common.details";
import { HubLinkedDocumentSaveModel } from "client/Api/Hub/common.composer";
import { RewriteToolTargetKind } from "client/Api/AiAssistant/aiAssistant";

export type ViewSpecificHandlers = {
	backHandler?: () => void;
	beforeCloseHandler?: () => Promise<boolean>;
	beforeSwitchingCategoryHandler?: () => Promise<boolean>;
};

export type HubDialogContextData = {
	setViewSpecificHandlers(options: ViewSpecificHandlers | undefined): void;
};

export const HubDialogContext = createContext<HubDialogContextData>({
	setViewSpecificHandlers: outOfContextCall("HubDialogContext", "setViewSpecificHandlers")
});

export type ActiveFilter =
	| { kind: "author"; value: SelectedAuthor; }
	| { kind: "tag"; value: TagViewModel & { itemKind: HubItemKind; }; };

export type HubItemView = {
	source: HubItemDataSource;
} & ({
	kind: "overview";
} | {
	kind: "details";
	id: number;
});

export type HubCommunityView = {
	source: HubDataSource.Community;
} & ({
	kind: "my-feed";
} | {
	kind: "community";
	id: number;
} | {
	kind: "post";
	id: number;
});

export type HubView =
	| HubItemView
	| HubCommunityView;

export type HubViewChangeHandler = (view: HubView) => void;

export function getFollowCategorySubscriptionStatus(
	categories: readonly HubItemCategoryModel[],
	categoryId: number
): boolean {
	const category: HubItemCategoryModel | undefined = categories.find(c => c.id === categoryId);

	if (!category) {
		console.error(`Category with id ${categoryId} not found`);
		return false;
	}

	return category.isFollowing;
}

export function getKnowledgeItemCategoryImageUrl(
	imageId: string
): string {
	return createUrlRelativeToEnvironment(`/Hub/KnowledgeItems/CategoryImage/?id=${encodeURIComponent(imageId)}`);
}

export function getKnowledgeItemGroupImageUrl(
	imageId: string
): string {
	return createUrlRelativeToEnvironment(`/Hub/KnowledgeItems/GroupImage/?id=${encodeURIComponent(imageId)}`);
}

export function getCommentsSortDirection(
	commentsSortPreference: CommentsSortPreference
): SortDirection {
	switch (commentsSortPreference) {
		case CommentsSortPreference.NewestOnTop:
			return SortDirection.Descending;
		case CommentsSortPreference.OldestOnTop:
			return SortDirection.Ascending;
		default:
			never(commentsSortPreference);
	}
}

export function getStructuredComments(
	comments: readonly HubCommentModel[],
	commentsSortPreference?: CommentsSortPreference
): readonly CommentWithRepliesModel[] {
	const [rootComments, replies] = partition(
		comments,
		c => isNil(c.parentCommentId)
	);

	const groupedReplies: Partial<Record<number, HubCommentModel[]>> = groupBy(
		replies,
		c => ensureNotNil(c.parentCommentId)
	);

	const commentsWithReplies: readonly CommentWithRepliesModel[] = rootComments.map(
		(c: HubCommentModel): CommentWithRepliesModel => {
			const replies: HubCommentModel[] | undefined = groupedReplies[c.id];

			return {
				...c,
				replies: isNotNil(replies)
					? sortBy(
						replies,
						c => c.publicationTime
					)
					: undefined
			};
		}
	);

	return isNil(commentsSortPreference)
		? commentsWithReplies
		: sortBy(
			commentsWithReplies,
			c => c.publicationTime,
			getCommentsSortDirection(commentsSortPreference)
		);
}

export type ImageUrlWithAttribution = {
	url: string;
	attribution?: ImageAttributionViewModel | null;
};

export type HubItemForImageViewer = {
	media: HubItemMediaDetailsViewModel;
	layout: HubItemLayoutDetailsViewModel;
};

export const listHubImageUrlWithAttributions = (
	source: HubDataSource,
	hubItem: HubItemForImageViewer | nil
): readonly ImageUrlWithAttribution[] => {
	if (!hubItem) {
		return [];
	}

	const result: ImageUrlWithAttribution[] = [];
	const { media, layout } = hubItem;

	if (media.mediaKind === HubItemMediaKind.Image) {
		const mediaUrl: string = getHubImageUrl(source, media.imageId);
		result.push({
			url: mediaUrl,
			attribution: media.attribution
		});
	}

	for (const row of layout.rows) {
		for (const column of row.columns) {
			for (const element of column.elements) {
				if (element.kind === HubItemElementKind.Image) {
					const mediaUrl: string = getHubImageUrl(source, element.blobId);
					result.push({
						url: mediaUrl,
						attribution: element.attribution
					});
				}
			}
		}
	}

	return result;
};

export type HubItemIsNewFilter = "all" | "new";

export const hubItemIsNewToFlag: { [key in HubItemIsNewFilter]: boolean | null; } = {
	"all": null,
	"new": true
};

export function formatNewItemsCount(value: number): string | undefined {
	if (value > 99) {
		return "99+";
	}

	if (value > 0) {
		return value.toString(10);
	}

	if (value === 0) {
		return undefined;
	}

	throw new Error("Negative unread counter is not supported.");
}

export function getGroupTotalNewItemsCount(
	group: HubItemGroupHubSidebarNodeModel
): number {
	let total = 0;

	for (const child of group.children) {
		switch (child.kind) {
			case HubItemCategoryNodeKind.Category:
				total += child.newItemsCount;
				break;
			case HubItemCategoryNodeKind.Group:
				total += getGroupTotalNewItemsCount(child);
				break;
			default:
				never(child);
		}
	}

	return total;
}

export type LinkedDocumentFormData = HubLinkedDocumentSaveModel & {
	extension: string;
};

export const hubItemKindToRewriteToolTargetKindMap: { [key in HubItemKind]: RewriteToolTargetKind; } = {
	[HubItemKind.Announcement]: RewriteToolTargetKind.Announcement,
	[HubItemKind.KnowledgeItem]: RewriteToolTargetKind.KnowledgeItem,
	[HubItemKind.Event]: RewriteToolTargetKind.Event
};